import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEntitlement } from "@/lib/checkout";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Gate the main app behind the lifetime entitlement.
 *
 * Behaviour:
 *  - While the entitlement query is loading we render a barely-visible
 *    splash (no UI flash) — the gate is meant to be invisible to paid
 *    users.
 *  - Demo mode bypasses the gate so the guided tour can run on the
 *    home / kanji / journal routes during the marketing demo.
 *  - Anonymous (no Google session) AND unpaid → bounce to /welcome.
 *  - Authenticated AND unpaid → bounce to /welcome (Stripe button +
 *    "you're already signed in" hint there).
 *  - Paid → render children.
 *
 * The gate is intentionally hoisted to App-route level rather than
 * scattered through each page so we have a single source of truth.
 */
export default function EntitlementGate({ children, demoActive }) {
  const { lifetime, isLoading } = useEntitlement();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // Demo bypass — the demo IS the paywall hook.
  const bypass = demoActive;

  useEffect(() => {
    if (bypass) return;
    if (isLoading) return;
    if (lifetime) return;
    // Preserve the original target so /welcome can offer "continue
    // where you were going" once they pay. (Hook for later — current
    // welcome page just shows the CTA.)
    const next = encodeURIComponent(pathname + (search || ""));
    navigate(`/welcome?next=${next}`, { replace: true });
  }, [bypass, isLoading, lifetime, pathname, search, navigate]);

  if (bypass) return children;
  if (isLoading || !lifetime) {
    return (
      <div
        data-testid="entitlement-gate-splash"
        className="min-h-[60vh] flex items-center justify-center text-sumiSoft font-sans text-xs uppercase tracking-[0.3em]"
      >
        {!user && !isLoading
          ? "checking access…"
          : "·"}
      </div>
    );
  }
  return children;
}
